import { nanoid } from 'nanoid/non-secure';
import {
  BundleAppProxy,
  DynamicBundleItemAppProxy,
  BundleSelection,
  BundleSelectionListParams,
  BundleSelectionsResponse,
  CreateBundleSelectionRequest,
  Session,
  UpdateBundleSelectionRequest,
  UpdateBundlePurchaseItem,
  BundlePurchaseItem,
  BundlePurchaseItemParams,
  BundleData,
} from '../types';
import { getInternalSession, rechargeApiRequest, shopifyAppProxyRequest } from '../utils/request';
import { getOptions } from '../utils/options';
import { BundleSelectionValidator, toLineItemProperty } from '../utils/bundle';

const STORE_FRONT_MANAGER_URL = '/bundling-storefront-manager';

function createBundleData(bundle: BundleAppProxy) {
  return toLineItemProperty({
    variantId: bundle.externalVariantId,
    version: 0, // server will replace it with the current time
    items: bundle.selections.map(item => {
      return {
        collectionId: item.collectionId,
        productId: item.externalProductId,
        variantId: item.externalVariantId,
        quantity: item.quantity,
        sku: '',
      };
    }),
  });
}

export async function getBundleId(bundle: BundleAppProxy): Promise<string> {
  const opts = getOptions();
  const isValid = await validateBundle(bundle);
  if (isValid !== true) {
    throw new Error(isValid);
  }
  const bundleData = createBundleData(bundle);

  try {
    const payload = await shopifyAppProxyRequest<{ id: string; code: number; message: string }>(
      'post',
      `${STORE_FRONT_MANAGER_URL}/api/v1/bundles`,
      {
        data: {
          bundle: bundleData,
        },
        headers: {
          Origin: `https://${opts.storeIdentifier}`,
        },
      }
    );

    if (!payload.id || payload.code !== 200) {
      throw new Error(`1: failed generating rb_id: ${JSON.stringify(payload)}`);
    }

    return payload.id;
  } catch (e) {
    // Handle NetworkError exceptions
    throw new Error(`2: failed generating rb_id ${e}`);
  }
}

export async function getBundleSelectionId(session: Session, bundle: BundleAppProxy): Promise<string> {
  const isValid = await validateBundle(bundle);
  if (isValid !== true) {
    throw new Error(isValid);
  }
  const bundleData = createBundleData(bundle);

  try {
    const payload = await rechargeApiRequest<{ id: string; code: number; message: string }>(
      'post',
      `/bundle_selections/bundle_id`,
      {
        data: {
          bundle: bundleData,
        },
      },
      getInternalSession(session, 'getBundleSelectionId')
    );

    if (!payload.id || payload.code !== 200) {
      throw new Error(`1: failed generating rb_id: ${JSON.stringify(payload)}`);
    }

    return payload.id;
  } catch (e) {
    // Handle NetworkError exceptions
    throw new Error(`2: failed generating rb_id ${e}`);
  }
}

export function getDynamicBundleItems(bundle: BundleAppProxy, shopifyProductHandle: string) {
  const isValid = validateDynamicBundle(bundle);
  if (isValid !== true) {
    throw new Error(`Dynamic Bundle is invalid. ${isValid}`);
  }
  // generate unique id for dynamic bundle
  const bundleId = `${nanoid(9)}:${bundle.externalProductId}`;
  return bundle.selections.map(item => {
    const itemData: DynamicBundleItemAppProxy = {
      id: item.externalVariantId,
      quantity: item.quantity,
      properties: {
        _rc_bundle: bundleId,
        _rc_bundle_variant: bundle.externalVariantId,
        _rc_bundle_parent: shopifyProductHandle,
        _rc_bundle_collection_id: item.collectionId,
      },
    };

    if (item.sellingPlan) {
      // this is used by SCI stores
      itemData.selling_plan = item.sellingPlan;
    } else if (item.shippingIntervalFrequency) {
      // this is used by RCS stores
      itemData.properties.shipping_interval_frequency = item.shippingIntervalFrequency;
      itemData.properties.shipping_interval_unit_type = item.shippingIntervalUnitType;
      itemData.id = `${item.discountedVariantId}`;
    }

    return itemData;
  });
}

export async function validateBundle(bundle: BundleAppProxy): Promise<true | string> {
  try {
    // once we implement this function, we can make it raise an exception
    // we could also have a local store relative to this function so we don't have to pass bundleProduct
    if (!bundle) {
      return 'Bundle is not defined';
    }
    if (bundle.selections.length === 0) {
      return 'No selections defined';
    }
    // Don't make bundle settings call due to merchant issues
    // const bundleSettings = await getCDNBundleSettings(bundle.externalProductId);
    // if (!bundleSettings) {
    //   return 'Bundle settings do not exist for the given product';
    // }
    return true;
  } catch (e) {
    return `Error fetching bundle settings: ${e}`;
  }
}

export async function validateBundleSelection(bundle: BundleAppProxy): Promise<{ valid: boolean; error?: string }> {
  try {
    const bundleData = await shopifyAppProxyRequest<BundleData>('get', `/bundle-data/${bundle.externalProductId}`);

    const selectionValidator = new BundleSelectionValidator(bundle.selections, bundleData);

    return { valid: selectionValidator.isBundleSelectionValid(bundle.externalVariantId) };
  } catch (error) {
    return { valid: false, error: String(error) };
  }
}

const intervalUnitGroups = {
  day: ['day', 'days', 'Days'],
  days: ['day', 'days', 'Days'],
  Days: ['day', 'days', 'Days'],
  week: ['week', 'weeks', 'Weeks'],
  weeks: ['week', 'weeks', 'Weeks'],
  Weeks: ['week', 'weeks', 'Weeks'],
  month: ['month', 'months', 'Months'],
  months: ['month', 'months', 'Months'],
  Months: ['month', 'months', 'Months'],
};

/**
 * Validates a dynamic bundle
 *
 * @param bundle Dynamic Bundle being validated
 * @returns true or error message
 */
export function validateDynamicBundle(bundle: BundleAppProxy): true | string {
  if (!bundle) {
    return 'No bundle defined.';
  }
  if (bundle.selections.length === 0) {
    return 'No selections defined.';
  }
  // validation for RCS onetimes
  const { shippingIntervalFrequency, shippingIntervalUnitType } =
    bundle.selections.find(selection => selection.shippingIntervalFrequency || selection.shippingIntervalUnitType) ||
    {};
  if (shippingIntervalFrequency || shippingIntervalUnitType) {
    // if we have shipping intervals then we should have both defined
    if (!shippingIntervalFrequency || !shippingIntervalUnitType) {
      return 'Shipping intervals do not match on selections.';
    } else {
      // if we have shipping intervals then any that are defined should match
      const shippingIntervalUnitGroup = intervalUnitGroups[shippingIntervalUnitType];
      for (let x = 0; x < bundle.selections.length; x++) {
        const { shippingIntervalFrequency: frequency, shippingIntervalUnitType: unitType } = bundle.selections[x];
        if (
          (frequency && frequency !== shippingIntervalFrequency) ||
          (unitType && !shippingIntervalUnitGroup.includes(unitType))
        ) {
          return 'Shipping intervals do not match on selections.';
        }
      }
    }
  }
  return true;
}

export async function getBundleSelection(session: Session, id: string | number): Promise<BundleSelection> {
  if (id === undefined || id === '') {
    throw new Error('ID is required');
  }
  const { bundle_selection } = await rechargeApiRequest<{ bundle_selection: BundleSelection }>(
    'get',
    `/bundle_selections`,
    {
      id,
    },
    getInternalSession(session, 'getBundleSelection')
  );
  return bundle_selection;
}

export function listBundleSelections(
  session: Session,
  query?: BundleSelectionListParams
): Promise<BundleSelectionsResponse> {
  return rechargeApiRequest<BundleSelectionsResponse>(
    'get',
    `/bundle_selections`,
    { query },
    getInternalSession(session, 'listBundleSelections')
  );
}

export async function createBundleSelection(
  session: Session,
  createRequest: CreateBundleSelectionRequest
): Promise<BundleSelection> {
  const { bundle_selection } = await rechargeApiRequest<{ bundle_selection: BundleSelection }>(
    'post',
    `/bundle_selections`,
    {
      data: createRequest,
    },
    getInternalSession(session, 'createBundleSelection')
  );
  return bundle_selection;
}

export async function updateBundleSelection(
  session: Session,
  id: string | number,
  updateRequest: UpdateBundleSelectionRequest
): Promise<BundleSelection> {
  if (id === undefined || id === '') {
    throw new Error('ID is required');
  }
  const { bundle_selection } = await rechargeApiRequest<{ bundle_selection: BundleSelection }>(
    'put',
    `/bundle_selections`,
    {
      id,
      data: updateRequest,
    },
    getInternalSession(session, 'updateBundleSelection')
  );
  return bundle_selection;
}

export function deleteBundleSelection(session: Session, id: string | number): Promise<void> {
  if (id === undefined || id === '') {
    throw new Error('ID is required');
  }
  return rechargeApiRequest<void>(
    'delete',
    `/bundle_selections`,
    {
      id,
    },
    getInternalSession(session, 'deleteBundleSelection')
  );
}

export async function updateBundle(
  session: Session,
  purchase_item_id: string | number,
  updateRequest: UpdateBundlePurchaseItem,
  query?: BundlePurchaseItemParams
): Promise<BundlePurchaseItem> {
  if (purchase_item_id === undefined || purchase_item_id === '') {
    throw new Error('Purchase item ID is required');
  }
  const { subscription } = await rechargeApiRequest<{ subscription: BundlePurchaseItem }>(
    'put',
    '/bundles',
    {
      id: purchase_item_id,
      data: updateRequest,
      query,
    },
    getInternalSession(session, 'updateBundle')
  );

  return subscription;
}
